import PropTypes from 'prop-types'
import Select from 'react-tailwindcss-select'
import { useTranslation } from 'react-i18next'

const SelectField = ({
  id = 'location',
  label = undefined,
  description = undefined,
  options = [],
  className,
  onChange,
  defaultValue = [],
  disabled = false,
  translate = false,
  isMultiple = false,
  optionsPosition = 'bottom',
  minSelections = 0,
  required = false
}) => {
  const { t } = useTranslation()
  // Konvertiere die Optionen in das Format, das react-tailwindcss-select erwartet
  const selectOptions = options
    .filter((option) => option && option.value) // Filtere ungültige Optionen
    .map((option) => ({
      value: option.id || '',
      label: option.value || ''
    }))

  // Konvertiere die defaultValue in das Format, das react-tailwindcss-select erwartet
  const selectValue = isMultiple
    ? defaultValue
        .map((value) => {
          const option = options.find((opt) => opt && opt.id === value)
          return option
            ? { value: option.id || '', label: option.value || '' }
            : null
        })
        .filter(Boolean)
    : (() => {
        const option = options.find((opt) => opt && opt.id === defaultValue)
        return option
          ? { value: option.id || '', label: option.value || '' }
          : null
      })()

  const handleChange = (values) => {
    if (isMultiple) {
      // Für Multi-Select: Array von IDs zurückgeben
      const selectedValues = values
        ? values.map((v) => v.value).filter(Boolean)
        : []
      onChange(selectedValues)
    } else {
      // Für Single-Select: Einzelne ID oder null zurückgeben
      onChange(values ? values.value : null)
    }
  }

  return (
    <div className={className}>
      {label && (
        <div className='mb-1'>
          <label className='block text-sm font-medium text-gray-700'>
            {label}
            {required && <span className='text-red-500 ml-1'>*</span>}
          </label>
          {isMultiple && (
            <div className='text-blue-600 text-xs font-medium mt-1'>
              ✓ {t('select_field.multiple_selection')}
            </div>
          )}
        </div>
      )}
      <Select
        value={selectValue}
        onChange={handleChange}
        options={selectOptions}
        isMultiple={isMultiple}
        isDisabled={disabled}
        primaryColor='indigo'
        isSearchable={false}
        loading={false}
        isClearable={true}
        formatOptionLabel={(option) => option?.label || ''}
      />
      {description && (
        <p className='text-gray-700 text-xs mt-2 italic'>{description}</p>
      )}
    </div>
  )
}

SelectField.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  description: PropTypes.string,
  options: PropTypes.array, // Array of { id, value } objects
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  defaultValue: PropTypes.oneOfType([
    PropTypes.array, // für Multi-Select
    PropTypes.string // für Single-Select
  ]),
  disabled: PropTypes.bool,
  translate: PropTypes.bool,
  isMultiple: PropTypes.bool,
  optionsPosition: PropTypes.string,
  minSelections: PropTypes.number,
  required: PropTypes.bool
}

export default SelectField
